/**
 * Badge visual de URL health en cards del Radar.
 *
 * - `unknown` — gris neutro, sin ruido
 * - `ok`      — verde discreto (emerald-50/700)
 * - `warn`    — amarillo (amber-50/700)
 * - `broken`  — rojo (red-50/700)
 *
 * Usa un tooltip nativo (`title`) con mensaje humano. Accesible vía
 * `aria-label` + role="status" para screen readers.
 */
import { classifyUrlHealth, type UrlHealthMeta } from "@/lib/urlHealth";
import { cn } from "@/lib/utils";

const STYLES_BY_STATUS: Record<UrlHealthMeta["status"], string> = {
  unknown: "bg-slate-100 text-slate-600",
  ok: "bg-cta-50 text-cta-700",
  warn: "bg-amber-50 text-amber-700",
  broken: "bg-red-50 text-red-700",
};

const DOT_BY_STATUS: Record<UrlHealthMeta["status"], string> = {
  unknown: "bg-slate-400",
  ok: "bg-cta-500",
  warn: "bg-amber-500",
  broken: "bg-red-500",
};

export interface UrlHealthBadgeProps {
  httpStatus: number | null | undefined;
  lastCheckedAt: string | null | undefined;
  className?: string;
}

export function UrlHealthBadge({ httpStatus, lastCheckedAt, className }: UrlHealthBadgeProps) {
  const meta = classifyUrlHealth(httpStatus, lastCheckedAt);

  return (
    <span
      role="status"
      aria-label={meta.tooltip}
      title={meta.tooltip}
      data-testid="url-health-badge"
      data-status={meta.status}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase",
        STYLES_BY_STATUS[meta.status],
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn("h-1.5 w-1.5 rounded-full", DOT_BY_STATUS[meta.status])}
      />
      {meta.label}
    </span>
  );
}
